import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Download, FileText, Scale, Shield, AlertTriangle,
  CheckCircle, ExternalLink, Printer, Mail
} from 'lucide-react';

const RightsToolbox = () => {
  const [downloadedItems, setDownloadedItems] = useState(new Set());

  const templates = [
    {
      id: 1,
      name: "Treaty Enforcement Letter",
      description: "Formal letter template to assert treaty rights when they are being violated or ignored.",
      category: "Enforcement",
      pages: 2,
      useCase: "State agencies not recognizing hunting/fishing rights",
      difficulty: "Intermediate",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/063cp97m_Treaty%20Enforcement%20Letter.pdf",
      fileSize: "0.08 MB"
    },
    {
      id: 2,
      name: "Notice of Violation to State Agent",
      description: "Official notice template to inform state officials of constitutional violations regarding tribal rights.",
      category: "Legal Notice",
      pages: 3,
      useCase: "Unlawful detention or taxation by state authorities",
      difficulty: "Advanced",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/bq6mgezv_Notice%20of%20Violation%20to%20State%20Agent.pdf",
      fileSize: "3.71 MB"
    },
    {
      id: 3,
      name: "Tribal ID Assertion Letter",
      description: "Template for asserting Private Membership Association or tribal identification rights.",
      category: "Identity",
      pages: 3,
      useCase: "Travel, identification, or business licensing issues",
      difficulty: "Beginner",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/22jx920o_Tribal%20ID%20Assertion%20Letter.pdf",
      fileSize: "3.78 MB"
    },
    {
      id: 4,
      name: "Religious Freedom Protection Notice",
      description: "Assert religious and cultural practice protections under federal law.",
      category: "Religious Rights",
      pages: 3,
      useCase: "Sacred site access or ceremonial practice restrictions",
      difficulty: "Intermediate",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/6satd73a_Religious%20Freedom%20Protection%20Notice.pdf",
      fileSize: "3.82 MB"
    }
  ];

  const legalDocuments = [
    {
      id: 1,
      name: "UN Declaration on Rights of Indigenous Peoples",
      description: "Complete text of the United Nations Declaration on the Rights of Indigenous Peoples",
      type: "International Law",
      pages: 18,
      year: 2007,
      importance: "Foundation for global Indigenous rights",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/jvtx2xn9_DRIPS_en.pdf",
      fileSize: "0.16 MB"
    },
    {
      id: 2,
      name: "English Bill of Rights (Historical Context)",
      description: "Historical document showing precedent for rights declarations and governmental limitations",
      type: "Historical Reference",
      pages: 8,
      year: 1689,
      importance: "Constitutional foundation for rights protections",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/ynmqgt91_English-Bill-of-Rights1%20%282%29.pdf",
      fileSize: "0.19 MB"
    },
    {
      id: 3,
      name: "U.S. Constitution + Native American Amendments",
      description: "Full Constitution with focus on tribal-relevant amendments and clauses",
      type: "Constitutional Law",
      pages: 35,
      year: 1787,
      importance: "Supreme law of the land regarding tribal relations",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/vlc70gwe_US%20Constitution%20copy.pdf",
      fileSize: "0.37 MB"
    },
    {
      id: 4,
      name: "Indian Civil Rights Act",
      description: "Federal law extending certain constitutional protections to tribal members",
      type: "Federal Statute",
      pages: 12,
      year: 1968,
      importance: "Bridge between federal and tribal civil rights",
      pdfUrl: "https://customer-assets.emergentagent.com/job_indigenous-vault-1/artifacts/jyecqq33_Indian%20Civil%20Rights%20Act.pdf",
      fileSize: "0.06 MB"
    }
  ];

  const handleDownload = (itemId, category) => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.error('Download not available in this environment');
      return;
    }

    if (category === 'template') {
      const template = templates.find(t => t.id === itemId);
      if (template && template.pdfUrl) {
        try {
          // Create a temporary link element and trigger download
          const link = document.createElement('a');
          link.href = template.pdfUrl;
          link.download = `${template.name.replace(/[^a-z0-9]/gi, '_')}.pdf`;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Mark as downloaded
          setDownloadedItems(prev => new Set([...prev, `${category}-${itemId}`]));
        } catch (error) {
          console.error('Error downloading template:', error);
          // Fallback: open in new tab
          window.open(template.pdfUrl, '_blank', 'noopener,noreferrer');
        }
      }
    } else if (category === 'document') {
      const documentItem = legalDocuments.find(d => d.id === itemId);
      if (documentItem && documentItem.pdfUrl) {
        try {
          // Create a temporary link element and trigger download
          const link = document.createElement('a');
          link.href = documentItem.pdfUrl;
          link.download = `${documentItem.name.replace(/[^a-z0-9]/gi, '_')}.pdf`;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Mark as downloaded
          setDownloadedItems(prev => new Set([...prev, `${category}-${itemId}`]));
        } catch (error) {
          console.error('Error downloading document:', error);
          // Fallback: open in new tab
          window.open(documentItem.pdfUrl, '_blank', 'noopener,noreferrer');
        }
      }
    } else {
      // Fallback for other categories
      setDownloadedItems(prev => new Set([...prev, `${category}-${itemId}`]));
      alert(`Downloading ${category === 'template' ? 'template' : 'document'}... (This is a demo)`);
    }
  };

  const handlePreview = (itemId) => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      console.error('Preview not available in this environment');
      return;
    }

    const template = templates.find(t => t.id === itemId);
    if (template && template.pdfUrl) {
      try {
        // Open PDF in new tab for preview
        window.open(template.pdfUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error opening preview:', error);
        // Fallback: try direct navigation
        if (window.location) {
          window.location.href = template.pdfUrl;
        }
      }
    }
  };

  const handlePreviewDocument = (itemId) => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      console.error('Preview not available in this environment');
      return;
    }

    const documentItem = legalDocuments.find(d => d.id === itemId);
    if (documentItem && documentItem.pdfUrl) {
      try {
        // Open PDF in new tab for preview
        window.open(documentItem.pdfUrl, '_blank', 'noopener,noreferrer');
      } catch (error) {
        console.error('Error opening document preview:', error);
        // Fallback: try direct navigation
        if (window.location) {
          window.location.href = documentItem.pdfUrl;
        }
      }
    }
  };

  const isDownloaded = (itemId, category) => {
    return downloadedItems.has(`${category}-${itemId}`);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Enforcement': 'bg-red-100 text-red-800',
      'Legal Notice': 'bg-orange-100 text-orange-800',
      'Identity': 'bg-blue-100 text-blue-800',
      'Religious Rights': 'bg-purple-100 text-purple-800',
      'International Law': 'bg-green-100 text-green-800',
      'Historical Reference': 'bg-gray-100 text-gray-800',
      'Constitutional Law': 'bg-blue-100 text-blue-800',
      'Federal Statute': 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Beginner': 'bg-green-100 text-green-800',
      'Intermediate': 'bg-yellow-100 text-yellow-800',
      'Advanced': 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Know Your Rights Toolbox</h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Download legal templates, foundational documents, and enforcement tools to protect and assert your tribal rights.
        </p>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="templates" className="flex items-center">
            <FileText className="w-4 h-4 mr-1" />
            Legal Templates
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center">
            <Scale className="w-4 h-4 mr-1" />
            Foundation Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="bg-white shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-gray-900 mb-2">
                        {template.name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge className={getCategoryColor(template.category)}>
                          {template.category}
                        </Badge>
                        <Badge className={getDifficultyColor(template.difficulty)}>
                          {template.difficulty}
                        </Badge>
                        <Badge variant="outline">
                          {template.pages} page{template.pages > 1 ? 's' : ''}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {template.fileSize}
                        </Badge>
                      </div>
                    </div>
                    {isDownloaded(template.id, 'template') && (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-700 text-sm mb-4">
                    {template.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Common Use Case:</h4>
                    <p className="text-gray-600 text-sm">{template.useCase}</p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleDownload(template.id, 'template')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isDownloaded(template.id, 'template') ? 'Downloaded' : 'Download PDF'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handlePreview(template.id)}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {legalDocuments.map((doc) => (
              <Card key={doc.id} className="bg-white shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <CardTitle className="text-lg text-gray-900 mb-2">
                        {doc.name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge className={getCategoryColor(doc.type)}>
                          {doc.type}
                        </Badge>
                        <Badge variant="outline">
                          {doc.year}
                        </Badge>
                        <Badge variant="outline">
                          {doc.pages} pages
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {doc.fileSize}
                        </Badge>
                      </div>
                    </div>
                    {isDownloaded(doc.id, 'document') && (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    )}
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-700 text-sm mb-4">
                    {doc.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Legal Importance:</h4>
                    <p className="text-gray-600 text-sm">{doc.importance}</p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => handleDownload(doc.id, 'document')}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isDownloaded(doc.id, 'document') ? 'Downloaded' : 'Download PDF'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handlePreviewDocument(doc.id)}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Usage Guidelines */}
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2 text-amber-600" />
            Important Usage Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Before Using Templates</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                  Research your specific tribal enrollment and rights
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                  Understand applicable treaties for your lineage
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                  Consult with tribal legal departments when possible
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                  Document all interactions and responses
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Legal Disclaimer</h3>
              <div className="flex items-start p-3 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 mr-3 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800">
                    These templates are educational tools and should not be considered legal advice. 
                    Tribal rights and federal Indian law are complex areas requiring specialized knowledge. 
                    Always consult with qualified attorneys when dealing with legal matters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* "Need help getting started" footer removed per user request */}
    </div>
  );
};

export default RightsToolbox;